// M09 - EducaFilesAPI
//
// Cliente para los endpoints /api/v1/admin/educa-files y el proxy
// publico /api/v1/educa-files/:id/stream.
//
// El flujo de subida son TRES pasos:
//   1) createUploadUrl -> server crea el EducaFile pending y emite una
//      signed URL PUT v4 para GCS.
//   2) uploadToSignedUrl -> el browser hace PUT directo a GCS (no pasa
//      por Cloud Run, asi esquivamos el 413). Soporta onProgress via XHR.
//   3) confirmUpload -> server lee metadata real de GCS y marca ready.

import { ENV } from "../Utils";

const BASE = `${ENV.BASE_API}/admin/educa-files`;
const STREAM_BASE = `${ENV.BASE_API}/educa-files`;

function headers(token, extra = {}) {
  return {
    Authorization: `Bearer ${token}`,
    ...extra,
  };
}

async function asJson(response) {
  let body = null;
  try {
    body = await response.json();
  } catch (e) {
    body = null;
  }
  if (!response.ok) {
    const err = new Error((body && body.message) || `HTTP ${response.status}`);
    err.status = response.status;
    err.body = body;
    throw err;
  }
  return body;
}

export class EducaFilesAPI {
  /* -------------------- Carpetas -------------------- */

  async listFolders(token) {
    const r = await fetch(`${BASE}/folders`, { headers: headers(token) });
    return asJson(r);
  }

  async createFolder(token, name) {
    const r = await fetch(`${BASE}/folders`, {
      method: "POST",
      headers: headers(token, { "Content-Type": "application/json" }),
      body: JSON.stringify({ name }),
    });
    return asJson(r);
  }

  async deleteFolder(token, name) {
    const r = await fetch(`${BASE}/folders/${encodeURIComponent(name)}`, {
      method: "DELETE",
      headers: headers(token),
    });
    return asJson(r);
  }

  /* -------------------- Ficheros -------------------- */

  async listFiles(token, query = {}) {
    const qs = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.append(k, v);
    });
    const url = qs.toString() ? `${BASE}?${qs.toString()}` : BASE;
    const r = await fetch(url, { headers: headers(token) });
    return asJson(r);
  }

  async getFile(token, id) {
    const r = await fetch(`${BASE}/${id}`, { headers: headers(token) });
    return asJson(r);
  }

  async updateFile(token, id, patch) {
    const r = await fetch(`${BASE}/${id}`, {
      method: "PATCH",
      headers: headers(token, { "Content-Type": "application/json" }),
      body: JSON.stringify(patch),
    });
    return asJson(r);
  }

  async deleteFile(token, id) {
    const r = await fetch(`${BASE}/${id}`, {
      method: "DELETE",
      headers: headers(token),
    });
    return asJson(r);
  }

  /* -------------------- Subida en 3 pasos -------------------- */

  async createUploadUrl(token, payload) {
    const r = await fetch(`${BASE}/upload-url`, {
      method: "POST",
      headers: headers(token, { "Content-Type": "application/json" }),
      body: JSON.stringify(payload),
    });
    return asJson(r);
  }

  // PUT directo a GCS con progress. Devuelve promesa que resuelve
  // cuando GCS responde 200. Usa XHR porque fetch no expone upload progress.
  uploadToSignedUrl({ url, file, contentType, onProgress, signal }) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url, true);
      xhr.setRequestHeader("Content-Type", contentType);
      if (xhr.upload && typeof onProgress === "function") {
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            onProgress(Math.round((ev.loaded / ev.total) * 100));
          }
        };
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ status: xhr.status });
        } else {
          reject(
            new Error(
              `GCS rechazo la subida (HTTP ${xhr.status}): ${xhr.responseText || ""}`,
            ),
          );
        }
      };
      xhr.onerror = () =>
        reject(new Error("Error de red durante la subida a GCS"));
      xhr.onabort = () => reject(new Error("Subida cancelada"));
      if (signal && typeof signal.addEventListener === "function") {
        signal.addEventListener("abort", () => xhr.abort());
      }
      xhr.send(file);
    });
  }

  async confirmUpload(token, id) {
    const r = await fetch(`${BASE}/${id}/confirm`, {
      method: "POST",
      headers: headers(token),
    });
    return asJson(r);
  }

  /* -------------------- URLs publicas -------------------- */

  // URL pensada para copiar y compartir externamente. Si el fichero es
  // public, abre sin auth. Si es private, exige header Authorization.
  streamUrl(id) {
    return `${STREAM_BASE}/${id}/stream`;
  }

  /* -------------------- Descarga -------------------- */

  // Pide al backend una signed URL GET v4 con Content-Disposition:
  // attachment. El browser puede descargar directo desde GCS sin pasar
  // por Cloud Run y sin necesidad de mandar Authorization (la firma va
  // embebida en la URL). Caduca a los pocos minutos.
  async getDownloadUrl(token, id) {
    const r = await fetch(`${BASE}/${id}/download-url`, {
      headers: headers(token),
    });
    return asJson(r);
  }
}
